/**
 * ProductPickerModal — generic marketplace product picker.
 *
 * Modal-style picker that reuses the management page's `<ProductTable>`
 * with the Actions column hidden. Platform-agnostic: callers pass
 * `platform` (tiktok / shopee / lazada / whatsapp) and the picker calls
 * the unified server endpoint `/v2/:platform/:shop_id/products`.
 *
 * Today only TikTok actually has the matching product-send wire on the
 * server; the picker shell works for any platform, but selecting a
 * product for an unsupported platform will fail at send time.
 *
 * Search behaviour:
 *  - Single combined input. Debounced 300 ms.
 *  - Filtering runs LOCALLY against a per-(platform, shop) in-memory
 *    cache so most keystrokes cost 0 server requests. Title substring
 *    (case-insensitive) OR exact-SKU match (incl. variant SKUs).
 *  - When the active query has too few matches AND the catalog isn't
 *    exhausted, an auto-scan loop fetches more pages until
 *    `SEARCH_TARGET_MATCHES` accumulated OR `SEARCH_MAX_PAGES` reached.
 *    Each fetched page is appended to the cache so subsequent searches
 *    reuse the work.
 *  - Mid-typing reset: the in-flight loop aborts and a new one starts
 *    against the now-larger cache (completed page fetches aren't wasted).
 *  - Refresh button drops the cached entry and refetches page 1.
 *  - SKU escape hatch: when the typed query looks like a full SKU AND
 *    the local cache has no match AND no scan is in progress, fires one
 *    `{ sku: query }` server request — the exact match (if any) is
 *    appended to the cache.
 *  - "Load more" fetches the next discovery page from the server,
 *    appends to cache, filter re-applies.
 */

import { FC, useEffect, useMemo, useRef, useState } from "react"
import type { Product } from "src/app/modules/product/types/ProductListing"
import productSearchClient, {
  type PickerPlatform,
} from "../../services/productSearchClient"
import productPickerCache from "../../services/productPickerCache"
import {
  SEARCH_MAX_PAGES,
  SEARCH_TARGET_MATCHES,
  SKU_FALLBACK_MIN_LENGTH,
} from "../../services/productPickerConfig"
import { useDebouncedValue } from "../../hooks/useDebouncedValue"
import ProductTable from "../../../product/components/listing/desktop/ProductTable/ProductTable"

export interface ProductPickerModalProps {
  platform: PickerPlatform
  shopId: string
  isOpen: boolean
  onClose: () => void
  onSelect: (product: Product) => void
}

const looksLikeSku = (q: string): boolean => {
  // Heuristic: pasted SKUs are usually long (≥8 chars) OR all digits.
  // Avoids firing the fallback on every short keystroke like "ear".
  if (!q) return false
  if (q.length >= SKU_FALLBACK_MIN_LENGTH) return true
  return /^\d+$/.test(q)
}

const filterLocally = (products: Product[], q: string): Product[] => {
  if (!q) return products
  const needle = q.toLowerCase()
  return products.filter((p) => {
    const titleMatch = p.title?.toLowerCase().includes(needle)
    const skuMatch = p.sku === q
    // Multi-variant products: also match an exact SKU against any variant's
    // seller_sku so pasting a colour/size SKU finds the parent product.
    const variantSkuMatch = p.variantCombinations?.some(
      (v) => v.sku === q
    )
    return titleMatch || skuMatch || variantSkuMatch
  })
}

const ProductPickerModal: FC<ProductPickerModalProps> = ({
  platform,
  shopId,
  isOpen,
  onClose,
  onSelect,
}) => {
  // Stable test-id prefix; rendered as `product-picker-${platform}-…`
  // so the same modal can coexist in multi-platform fixtures without
  // selector collisions.
  const tid = `product-picker-${platform}`

  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [isSkuFallbackPending, setIsSkuFallbackPending] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState("")
  const debouncedQuery = useDebouncedValue(query, 300)

  // Tracks the most recent in-flight scan. When debouncedQuery changes,
  // the previous scan flips its sentinel to `cancelled: true` before
  // awaiting another page — so partial work already merged into the
  // cache stays, but no further requests fire for the stale query.
  const scanTokenRef = useRef<{ cancelled: boolean } | null>(null)

  // Scroll-based "load more" — sentinel placed at the bottom of the list.
  // The IntersectionObserver fires when it scrolls into view.
  const listContainerRef = useRef<HTMLDivElement>(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)

  // SKU escape hatch — track which exact queries we've already tried so
  // a re-render or backspace doesn't refire the fallback.
  const skuFallbackTried = useRef<Set<string>>(new Set())

  /** Reload state from the cache (synchronous read of current entry). */
  const hydrateFromCache = (): boolean => {
    const cached = productPickerCache.get(platform, shopId)
    if (!cached) return false
    setProducts(cached.products)
    setNextCursor(cached.nextCursor)
    setHasMore(cached.hasMore)
    return true
  }

  /** Fetch page 1 from the server and replace the cache entry. */
  const fetchPageOne = async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await productSearchClient.searchProducts(
        platform,
        shopId,
        {}
      )
      productPickerCache.set(platform, shopId, {
        products: res.products,
        nextCursor: res.nextCursor,
        hasMore: res.hasMore,
      })
      setProducts(res.products)
      setNextCursor(res.nextCursor)
      setHasMore(res.hasMore)
    } catch (err: any) {
      setError(err?.message ?? "Failed to load products")
    } finally {
      setIsLoading(false)
    }
  }

  // On open or shop/platform change: try cache first; else fetch page 1.
  useEffect(() => {
    if (!isOpen) return
    setQuery("")
    setError(null)
    skuFallbackTried.current = new Set()
    if (scanTokenRef.current) scanTokenRef.current.cancelled = true
    if (!hydrateFromCache()) {
      void fetchPageOne()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, platform, shopId])

  // Local filter — pure derivation from cached products + debounced query.
  const visibleProducts = useMemo(
    () => filterLocally(products, debouncedQuery.trim()),
    [products, debouncedQuery]
  )

  // ── Auto-pagination scan: keep fetching pages until enough matches
  //    accumulate OR the safety cap is hit OR the catalog is exhausted.
  //
  //    IMPORTANT: deps are intentionally narrow — only `debouncedQuery`,
  //    `platform`, `shopId`, and `isLoading`. The scan reads cursor /
  //    hasMore from the cache directly on each iteration, so it doesn't
  //    need to re-run on `products` / `hasMore` state changes. Including
  //    those would self-cycle: each iteration's setProducts() would
  //    re-trigger the effect, cancel itself, and start over from page 0
  //    — making `SEARCH_MAX_PAGES` per-cycle instead of per-search and
  //    risking runaway pagination on deep catalogs.
  useEffect(() => {
    const q = debouncedQuery.trim()

    // Cancel any in-flight scan whenever the trigger inputs change.
    if (scanTokenRef.current) scanTokenRef.current.cancelled = true

    if (!q) return
    if (isLoading) return // page 1 still loading — let it land first

    // Read the freshest cache state at scan-start time.
    const initial = productPickerCache.get(platform, shopId)
    if (!initial) return // page 1 hasn't populated yet — guarded above too
    if (!initial.hasMore) return // catalog already exhausted in cache

    const matchesAtStart = filterLocally(initial.products, q)
    if (matchesAtStart.length >= SEARCH_TARGET_MATCHES) return

    const token = { cancelled: false }
    scanTokenRef.current = token

    let finished = false
    const run = async () => {
      setIsScanning(true)
      let cursor: string | null = initial.nextCursor
      let canContinue: boolean = initial.hasMore
      let pagesFetched = 0
      try {
        while (
          !token.cancelled &&
          pagesFetched < SEARCH_MAX_PAGES &&
          canContinue &&
          cursor
        ) {
          const res = await productSearchClient.searchProducts(
            platform,
            shopId,
            { cursor }
          )
          if (token.cancelled) return

          productPickerCache.append(
            platform,
            shopId,
            res.products,
            res.nextCursor,
            res.hasMore
          )
          const updated = productPickerCache.get(platform, shopId)
          if (!updated) return

          setProducts(updated.products)
          setNextCursor(updated.nextCursor)
          setHasMore(updated.hasMore)

          cursor = updated.nextCursor
          canContinue = updated.hasMore
          pagesFetched++

          const newMatches = filterLocally(updated.products, q)
          if (newMatches.length >= SEARCH_TARGET_MATCHES) break
        }
      } catch (err: any) {
        if (!token.cancelled) setError(err?.message ?? "Search failed")
      } finally {
        if (!token.cancelled) setIsScanning(false)
        finished = true
      }
    }

    void run()

    return () => {
      token.cancelled = true
      if (!finished) setIsScanning(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, platform, shopId, isLoading])

  // SKU escape hatch — fires only when:
  //   - query passes the SKU heuristic
  //   - no local matches
  //   - no scan in progress (auto-paginating may yet find it)
  //   - haven't already tried this exact query
  useEffect(() => {
    const q = debouncedQuery.trim()
    if (!q) return
    if (!looksLikeSku(q)) return
    if (isScanning) return
    if (visibleProducts.length > 0) return
    if (skuFallbackTried.current.has(q)) return

    skuFallbackTried.current.add(q)
    setIsSkuFallbackPending(true)
    productSearchClient
      .searchProducts(platform, shopId, { sku: q })
      .then((res) => {
        if (res.products.length === 0) return
        productPickerCache.append(
          platform,
          shopId,
          res.products,
          // SKU exact returns 0/1 row and doesn't continue the
          // discovery paginator — preserve the existing cursor.
          nextCursor,
          hasMore
        )
        const updated = productPickerCache.get(platform, shopId)
        if (updated) setProducts(updated.products)
      })
      .catch((err) => {
        setError(err?.message ?? "SKU lookup failed")
      })
      .finally(() => setIsSkuFallbackPending(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, visibleProducts.length, isScanning])

  // Scroll-trigger: when the bottom sentinel intersects the list viewport,
  // fire handleLoadMore. Click button stays as a fallback.
  //
  // IMPORTANT: this hook MUST sit before the `if (!isOpen) return null`
  // early return below — React's rules of hooks require the same set of
  // hooks to be called on every render, and conditionally bypassing this
  // useEffect when the picker is closed would change the hook count
  // between mount-as-closed and mount-as-open renders.
  //
  // `handleLoadMore` is declared after this hook (post-early-return); the
  // closure binds lazily, so by the time the IntersectionObserver actually
  // fires the const is initialised and reachable.
  useEffect(() => {
    if (!isOpen) return
    const sentinel = loadMoreSentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        if (!hasMore || isLoadingMore || isScanning) return
        void handleLoadMore()
      },
      {
        // Use the scrollable list container as the root so intersection
        // is measured against the list's own viewport, not the page.
        root: listContainerRef.current,
        // Trigger a bit before the user actually reaches the bottom so
        // pagination feels instant.
        rootMargin: '200px',
        threshold: 0,
      }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasMore, isLoadingMore, isScanning, products.length])

  if (!isOpen) return null

  const handlePick = (product: Product) => {
    onSelect(product)
    onClose()
  }

  const handleLoadMore = async () => {
    if (!hasMore || !nextCursor || isLoadingMore) return
    setIsLoadingMore(true)
    setError(null)
    try {
      const res = await productSearchClient.searchProducts(platform, shopId, {
        cursor: nextCursor,
      })
      productPickerCache.append(
        platform,
        shopId,
        res.products,
        res.nextCursor,
        res.hasMore
      )
      const updated = productPickerCache.get(platform, shopId)
      if (updated) {
        setProducts(updated.products)
        setNextCursor(updated.nextCursor)
        setHasMore(updated.hasMore)
      }
    } catch (err: any) {
      setError(err?.message ?? "Failed to load more products")
    } finally {
      setIsLoadingMore(false)
    }
  }

  const handleRefresh = () => {
    if (scanTokenRef.current) scanTokenRef.current.cancelled = true
    productPickerCache.invalidate(platform, shopId)
    skuFallbackTried.current = new Set()
    void fetchPageOne()
  }

  // Status pill: scanning > sku fallback > nothing.
  const statusLabel = isScanning
    ? "Scanning catalog…"
    : isSkuFallbackPending
    ? "Looking up SKU…"
    : ""

  // Empty-state copy.
  const activeQuery = debouncedQuery.trim().length > 0
  const emptyMessage = !activeQuery
    ? "No products."
    : isScanning
    ? "Scanning catalog…"
    : hasMore
    ? "No matches yet — Load more to keep searching."
    : "No matching products."

  return (
    <div
      data-testid={tid}
      role="dialog"
      aria-label="Search product"
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-z-[1060]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-7xl tw-mx-4 tw-flex tw-flex-col tw-overflow-hidden"
        style={{ maxHeight: "85vh" }}
      >
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-200">
          <span className="tw-text-base tw-font-semibold tw-text-gray-900">
            Search Product
          </span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isLoading}
              data-testid={`${tid}-refresh`}
              title="Refresh catalog (drop cache)"
              aria-label="Refresh"
              className="tw-text-gray-500 hover:tw-text-gray-800 tw-text-base tw-leading-none disabled:tw-text-gray-300 disabled:tw-cursor-not-allowed"
            >
              ⟳
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid={`${tid}-close`}
              className="tw-text-gray-500 hover:tw-text-gray-800 tw-text-xl tw-leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Combined search input — local filter; debounce 300ms */}
        <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or SKU"
            data-testid={`${tid}-search`}
            className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-text-gray-800 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-placeholder-gray-400"
          />
          {statusLabel && (
            <span
              className="tw-absolute tw-right-6 tw-top-1/2 tw--translate-y-1/2 tw-text-xs tw-text-gray-400"
              data-testid={`${tid}-status`}
            >
              {statusLabel}
            </span>
          )}
        </div>

        {/* Body — the management-page ProductTable, minus the Actions column */}
        <div
          ref={listContainerRef}
          className="tw-flex-1 tw-overflow-auto"
          data-testid={`${tid}-list`}
        >
          {error ? (
            <div
              data-testid={`${tid}-error`}
              className="tw-p-6 tw-text-center tw-text-sm tw-text-red-600"
            >
              {error}
            </div>
          ) : (
            <>
              <ProductTable
                products={visibleProducts}
                loading={isLoading}
                hideActions
                showVariants
                stickyHeader
                onRowClick={handlePick}
                onSortChange={() => { /* sorting deferred */ }}
                emptyMessage={emptyMessage}
              />
              {/* IntersectionObserver target for scroll-based load-more.
                  Sized to a single px so it doesn't visually intrude. */}
              <div
                ref={loadMoreSentinelRef}
                aria-hidden="true"
                data-testid={`${tid}-load-more-sentinel`}
                style={{ height: 1 }}
              />
            </>
          )}
        </div>

        {/* Pagination — fetches the next discovery page from server */}
        <div className="tw-px-4 tw-py-2 tw-border-t tw-border-gray-200 tw-text-center">
          <button
            type="button"
            disabled={!hasMore || isLoadingMore || isScanning}
            onClick={handleLoadMore}
            data-testid={`${tid}-load-more`}
            className={
              hasMore && !isLoadingMore && !isScanning
                ? "tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-cursor-pointer tw-font-medium"
                : "tw-text-xs tw-text-gray-400 tw-cursor-not-allowed"
            }
          >
            {isLoadingMore
              ? "Loading…"
              : hasMore
              ? "Load more"
              : "No more products"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProductPickerModal
